import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Toast from "../../component/ToastService";
import { StatusType } from "../../constants/Status";
import { signup } from "./services/LoginService";
import { set_logged_data } from "../../service/Configuration";
import GoogleIcon from "@mui/icons-material/Google";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Link as MuiLink,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';

function Signup() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirm_password: "",
    acceptTerms: false,
    subscribeNewsletter: false,
  });

  const [policyData, setPolicyData] = useState({
    privacy_policy: `
  Effective Date: June 25, 2025
  
  At Centipeak, we are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and protect your data when you sign up and use our services, including note-taking, spaced repetition tools, and newsletter features.
  
  1. Information We Collect
  - Email address (required for signup and login)
  - Notes and user-generated content you create
  - Usage data (e.g., IP address, browser type, access times)
  - Newsletter preferences and marketing consent
  
  2. How We Use Your Information
  - To create and manage your user account
  - To deliver personalized study and spaced repetition features
  - To send transactional and marketing emails (with your consent)
  - To improve service performance and security
  
  3. Legal Basis for Processing
  - Your consent (GDPR Art. 6(1)(a)) when you opt in to our newsletter
  - Performance of a contract (GDPR Art. 6(1)(b)) for delivering the core app features
  - Legitimate interests in securing and improving our service
  
  4. Data Retention
  - Your data is retained as long as your account is active or required by law.
  - You can request deletion of your account and associated data anytime.
  
  5. Sharing of Data
  - We do not sell your data.
  - We may share data with trusted service providers (e.g., cloud hosting, analytics) under strict confidentiality agreements.
  
  6. Your Rights
  - Access, correct, or delete your personal data
  - Withdraw consent for marketing at any time
  - Request data portability or lodge a complaint with a data protection authority
  
  7. Cookies and Tracking
  - We use minimal cookies for login sessions and service performance.
  - No third-party ad trackers are used.
  
  8. Data Security
  - Data is encrypted in transit (TLS) and at rest
  - Access controls, logging, and regular security updates are enforced
  
  9. International Transfers
  - Data may be processed outside your country, but we ensure adequate protections are in place.
  
  10. Contact Us
  If you have any questions about this Privacy Policy or your personal data, contact us at:  
  **Email:** info@centipeak.com
  
  By signing up, you agree to this Privacy Policy.
    `,
    terms_of_service: `
  Effective Date: June 25, 2025
  
  Welcome to Centipeak! These Terms of Service ("Terms") govern your use of our spaced repetition and learning platform.
  
  1. Acceptance of Terms
  By accessing or using the Centipeak platform, you agree to be bound by these Terms and our Privacy Policy.
  
  2. Use of the Service
  - You must be at least 13 years old or the legal age of digital consent in your jurisdiction.
  - You are responsible for all content you create, upload, or interact with.
  - Don’t misuse the service (e.g., spam, reverse engineering, unauthorized scraping).
  
  3. Account Security
  You are responsible for maintaining the confidentiality of your login credentials.
  
  4. Content Ownership
  You retain ownership of your notes and content. By using the platform, you grant Centipeak a non-exclusive license to process and display this content for service delivery.
  
  5. Modifications and Downtime
  We may update these Terms and features periodically. You will be notified of significant changes.
  
  6. Termination
  We reserve the right to suspend or terminate accounts for violations of these Terms.
  
  7. Limitation of Liability
  Centipeak is provided “as is.” We are not liable for indirect damages, including data loss or business interruption.
  
  8. Governing Law
  These Terms are governed by the laws of the Republic of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Pune, Maharashtra.
  
  9. Contact Us
  For any legal or compliance inquiries, email: info@Centipeak.com
  
  By continuing to use Centipeak, you agree to these Terms.
    `
  });
  
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [loadingPolicies, setLoadingPolicies] = useState(false);

  // Fetch policy links on mount
  useEffect(() => {
    setLoadingPolicies(true);
    fetch("/api/policies") // Replace with actual API endpoint
      .then(res => res.json())
      .then(data => {
        setPolicyData(data);
        setLoadingPolicies(false);
      })
      .catch(() => {
        // Toast(StatusType.ERROR, "Failed to load policies.");
        setLoadingPolicies(false);
      });
  }, []);

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const { email, password, confirm_password, acceptTerms } = formData;

    if (!email || !password || !confirm_password) {
      Toast(StatusType.ERROR, "Please fill all required fields.");
      return;
    }

    if (password !== confirm_password) {
      Toast(StatusType.ERROR, "Password and confirm password do not match.");
      return;
    }

    if (!acceptTerms) {
      Toast(StatusType.ERROR, "You must accept the Privacy Policy and Terms.");
      return;
    }

    try {
      const response = await signup(formData);
      const responseData = await response.json();

      if (response.ok) {
        set_logged_data(responseData.data.token);
        Toast(StatusType.SUCCESS, responseData.message);
        navigate("/app");
      } else {
        Toast(StatusType.ERROR, responseData.message);
      }
    } catch (error) {
      Toast(StatusType.ERROR, error.message);
    }
  };

  return (
    <Grid container sx={{ height: '100vh' }}>
      <Grid
        item
        md={6}
        sx={{
          display: { xs: 'none', md: 'flex' },
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, rgba(60, 179, 113, 0.8), rgba(2, 2, 36, 0.8))',
        }}
      >
        <Box sx={{ backgroundColor: "rgba(0, 0, 0, 0.5)", padding: 4, borderRadius: "10px", color: "white" }}>
          <Typography variant="h4" align="center">Join Centipeak Space Repetition</Typography>
          <Typography mt={2} variant="body1" align="center">
            Organize your notes, master memory, and retain forever.
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} md={6} container justifyContent="center" alignItems="center">
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: 4,
            width: 400,
            backdropFilter: 'blur(10px)',
            background: 'rgba(255, 255, 255, 0.85)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <form onSubmit={onSubmit}>
            <Typography variant="h4" align="center" fontWeight={600} mb={3} color="primary.dark">
              Sign Up
            </Typography>

            <TextField fullWidth margin="normal" label="Email Address" name="email" type="email" onChange={onInputChange} required />
            <TextField fullWidth margin="normal" label="Password" name="password" type="password" onChange={onInputChange} required />
            <TextField fullWidth margin="normal" label="Confirm Password" name="confirm_password" type="password" onChange={onInputChange} required />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.acceptTerms}
                  onChange={onInputChange}
                  name="acceptTerms"
                  required
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{" "}
                  <MuiLink component="button" type="button" onClick={() => setShowPolicyModal(true)} underline="hover" color="primary">
                    Privacy Policy & Terms
                  </MuiLink>
                </Typography>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.subscribeNewsletter}
                  onChange={onInputChange}
                  name="subscribeNewsletter"
                />
              }
              label="Subscribe to our newsletter"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, borderRadius: 3, py: 1.5 }}
            >
              Sign Up
            </Button>

            <Box textAlign="center">
              <Typography variant="body2" mb={1}>
                Or sign up with
              </Typography>
              <Button
                variant="outlined"
                startIcon={<GoogleIcon />}
                href={`${process.env.REACT_APP_BASE_PATH}/oauth2/authorization/google`}
              >
                Continue with Google
              </Button>
            </Box>

            <Box mt={4} textAlign="center">
              <Typography variant="body2">
                Already a member?{" "}
                <MuiLink component={Link} to="/auth/login" underline="hover" color="primary">
                  Login
                </MuiLink>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Grid>

      {/* Modal for Privacy & Terms */}
      <Dialog open={showPolicyModal} onClose={() => setShowPolicyModal(false)} fullWidth maxWidth="md">
        <DialogTitle>Privacy Policy & Terms</DialogTitle>
        <DialogContent dividers>
          {loadingPolicies ? (
            <Box display="flex" justifyContent="center"><CircularProgress /></Box>
          ) : (
            <>
            <Typography variant="h6" gutterBottom>Privacy Policy</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap", mb: 4 }}
            >
              {policyData.privacy_policy}
            </Typography>
          
            <Typography variant="h6" gutterBottom>Terms of Service</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {policyData.terms_of_service}
            </Typography>
          </>
          
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPolicyModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default Signup;
